import { Link, useSearchParams } from 'react-router-dom';
import { CatalogSection } from '@/features/publicsite/catalog/CatalogSection';
import { ListingFilterPanel } from '@/features/publicsite/ListingFilterPanel';
import { activeFilterTags } from '@/features/common/listingFilters';
import { publicRegistry } from '@/i18n/dictionaries/publicRegistry';
import { ADVISOR_DOMAINS } from '@/features/common/advisorDomains';
import { FilterSelect } from '@/features/common/FilterPanel/FilterSelect';
import { useNomenclatureItems } from '@/features/common/useNomenclatureItems';
import { domainLabel } from '@/features/common/useThematicDomains';
import { useTranslation } from '@/i18n/useTranslation';
import { AdvisorCard } from '@/features/publicsite/registry/AdvisorCard';
import { AdvisorCoverageGaps } from '@/features/publicsite/registry/AdvisorCoverageGaps';
import { AdvisorDomainCoverage } from '@/features/publicsite/registry/AdvisorDomainCoverage';
import { advisorFilters, withAdvisorFilters } from '@/features/publicsite/registry/advisorFilters';
import { AdvisorsEmptyMessage } from '@/features/publicsite/registry/AdvisorsEmptyMessage';
import { AdvisorsRegisterCta } from '@/features/publicsite/registry/AdvisorsRegisterCta';
import { AdvisorStatTiles } from '@/features/publicsite/registry/AdvisorStatTiles';
import { domainCoverageRows } from '@/features/publicsite/registry/domainCoverage';
import type { AdvisorFilterName } from '@/features/publicsite/types/registry/AdvisorFilterName';
import { useAdvisorCoverage } from '@/features/publicsite/registry/useAdvisorCoverage';
import { useAdvisors } from '@/features/publicsite/registry/useAdvisors';

/** The lists that get a tag under the bar when set — the registry has the one. */
const TAGGED = ['domain'] as const;

export function AdvisorsPage() {
  const { t, language } = useTranslation(publicRegistry);
  const [params, setParams] = useSearchParams();
  const filters = advisorFilters(params);
  const query = filters.q ?? '';
  const domain = filters.domain ?? '';
  const advisors = useAdvisors(query, domain);
  const coverage = useAdvisorCoverage();
  // Read once for the whole page: the filter offers these names and the coverage report is nothing
  // but ids without them. The fetch itself is shared per nomenclature code, so this is one request.
  const domainItems = useNomenclatureItems(ADVISOR_DOMAINS);
  const domainOptions = [
    { value: '', label: t('advisorsAllDomains') },
    ...domainItems.map((item) => ({ value: item.id, label: domainLabel(item, language) })),
  ];
  const apply = (changes: Partial<Record<AdvisorFilterName, string | null>>) =>
    setParams(withAdvisorFilters(params, changes), { preventScrollReset: true });
  // A chip on a card narrows to that domain by id, and the tag under the bar names it as the
  // list does — never the id the URL holds — with its own ✕, as /stiri and /evenimente show theirs.
  const selectDomain = (id: string) => apply({ domain: id });
  const tags = activeFilterTags(TAGGED, filters, { domain: domainOptions }).map((tag) => ({
    ...tag,
    onRemove: () => apply({ [tag.name]: null }),
  }));

  return (
    <CatalogSection
      // The template opened this page with an <h2> and never used an <h1>, so the document had no
      // top-level heading at all — a structure defect, not a style choice. Corrected here.
      eyebrow={t('eyebrowCatalog')}
      heading={t('advisorsHeading')}
      lead={t('advisorsLead')}
      items={advisors}
      // A count of zero says nothing about why. Named here rather than left to the shared line,
      // which reads "no records yet" — true of a registry nobody has filled in, wrong of a search
      // that excluded all 128 of them, and the reader cannot tell the two apart from a blank page.
      emptyMessage={<AdvisorsEmptyMessage filtered={Object.keys(filters).length > 0} />}
      renderCard={(a) => <AdvisorCard key={a.id} advisor={a} onDomainSelect={selectDomain} />}
    >
      {/* The badge is what tells a zero apart from a tile that failed to load: a number alone in a
          card is ambiguous, and these two load from different endpoints. */}
      <AdvisorStatTiles
        activeCount={advisors?.length ?? 0}
        zoneCount={coverage?.zones.length ?? 0}
      />

      {coverage && <AdvisorCoverageGaps coverage={coverage} />}
      {coverage && (
        <AdvisorDomainCoverage rows={domainCoverageRows(coverage, domainItems, language)} />
      )}
      <p className="muted advisors-coverage__note">
        {t('advisorsCoverageBefore')} <Link to="/harta">{t('advisorsCoverageLink')}</Link>
        {t('advisorsCoverageAfter')}
      </p>

      {/* The search bar /stiri and /evenimente have, with the one list this registry can be
          narrowed by: the domain of competence. No period — the registry has no dates to filter
          by. preventScrollReset, as there: the bar sits under the tiles and the coverage, and a
          search sent the page back to its top. */}
      <ListingFilterPanel
        ariaLabel={t('advisorsSearchLabel')}
        filters={filters}
        canReset={Object.keys(filters).length > 0}
        results=""
        period={false}
        searchLabel={t('advisorsSearchLabel')}
        searchPlaceholder={t('advisorsSearchPlaceholder')}
        variant="plain"
        tags={tags}
        onFilter={apply}
        onReset={() => setParams(new URLSearchParams(), { preventScrollReset: true })}
      >
        <FilterSelect
          name="domain"
          label={t('advisorsDomainFilter')}
          ariaLabel={t('advisorsDomainFilterAria')}
          placeholder={t('advisorsAllDomains')}
          options={domainOptions}
          value={domain}
          onChange={(value) => apply({ domain: value })}
          // Off only when there is nothing to choose and nothing to take off.
          disabled={domainItems.length === 0 && !domain}
          labelHidden
        />
      </ListingFilterPanel>

      <AdvisorsRegisterCta />

      <h2 className="advisors-available">{t('advisorsAvailable')}</h2>
    </CatalogSection>
  );
}
